// @refresh reset
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../Dashboard/Dashboard.css';
import './MeetingRecords.css';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import { openResumeInNewTab } from '../../utils/resumeViewer';
import filterIcon from '../../assets/filter.png';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject as GridInject, Sort, Filter, Toolbar as GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze } from '@syncfusion/ej2-react-grids';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useAuth } from '../../contexts/AuthContext';
import { useRoleMessages } from '../../contexts/RoleMessagesContext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { meetingRecordsAPI } from '../../services/meetingRecords';
import { listUsers } from '../../services/users';
import { listEvents } from '../../services/events';
import { listBooths } from '../../services/booths';
import { useRecruiterBooth } from '../../hooks/useRecruiterBooth';
import JobSeekerProfileModal from '../common/JobSeekerProfileModal';
import MeetingRecordDetailModal from './MeetingRecordDetailModal';
import { jobSeekerAvatarCellTemplate } from '../common/JobSeekerAvatarThumbnail';
import AdvancedJobSeekerSearch from '../JobSeekerManagement/AdvancedJobSeekerSearch';
import JSZip from 'jszip';
import { SYNC_GRID_FILTER_SETTINGS, SYNC_GRID_CHECKBOX_COLUMN_PROPS, labelsToFilterText } from '../../utils/syncfusionGridHelpers';
import {
    JOB_CATEGORY_LIST,
    EXPERIENCE_LEVEL_LIST,
    EDUCATION_LEVEL_LIST,
    JOB_TYPE_LIST,
    LANGUAGE_LIST,
    MILITARY_EXPERIENCE_LIST
} from '../../constants/options';
// Labels shown for each meeting status. The grid's Status column filters and sorts on these,
// so what people type matches what they see.
const STATUS_LABELS = {
    scheduled: 'Scheduled',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    failed: 'Failed',
    left_with_message: 'Left Message'
};

// Position and location chosen on the booth entrance form. Records saved before these were
// copied onto the meeting record fall back to the populated queue entry.
const getAppliedPosition = (record) => record?.appliedPosition || record?.queueId?.appliedPosition || '';
const getAppliedLocation = (record) => record?.appliedLocation || record?.queueId?.appliedLocation || '';

export default function MeetingRecords() {
    const { user, loading } = useAuth();
    const { getMessage } = useRoleMessages();
    const navigate = useNavigate();
    const location = useLocation();
    const { booth, event } = useRecruiterBooth();
    
    // Get role message from context
    const infoBannerMessage = getMessage('meeting-records', 'info-banner') || '';
    
    useEffect(() => {
        if (!loading) {
            if (!user) {
                navigate('/login', { replace: true });
            } else if (!['Admin', 'GlobalSupport', 'Recruiter'].includes(user.role)) {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [user, loading, navigate]);

    // Set CSS variable for filter icon and make it trigger column menu
    useEffect(() => {
        if (!gridRef.current) return;
        
        const filterIconUrl = `url(${filterIcon})`;
        
        // Set CSS variable on document root
        document.documentElement.style.setProperty('--filter-icon-url', filterIconUrl);
        
        const grid = gridRef.current;
        
        // Override filter icon click to open column menu instead
        const handleFilterIconClick = (e) => {
            const filterIcon = e.target.closest('.e-filtericon');
            if (!filterIcon) return;
            
            e.stopPropagation();
            e.preventDefault();
            
            const headerCell = filterIcon.closest('.e-headercell');
            if (!headerCell || !grid.columnMenuModule) return;
            
            // Get column field from header cell
            const columnIndex = Array.from(headerCell.parentElement.children).indexOf(headerCell);
            const column = grid.columns[columnIndex];
            
            if (column) {
                // Open column menu
                grid.columnMenuModule.openColumnMenu(headerCell, column, e);
            }
        };
        
        // The filter-icon image is styled entirely via CSS (--filter-icon-url), so no
        // per-mutation DOM observer is needed. We only intercept clicks on the grid
        // element to open the column menu. Runs once on mount.
        const gridElement = grid.element;
        if (gridElement) {
            gridElement.addEventListener('click', handleFilterIconClick, true);
        }
        
        return () => {
            document.documentElement.style.removeProperty('--filter-icon-url');
            if (gridElement) {
                gridElement.removeEventListener('click', handleFilterIconClick, true);
            }
        };
    }, []);

    const [meetingRecords, setMeetingRecords] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [events, setEvents] = useState([]);
    const [booths, setBooths] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const toastRef = useRef(null);
    const gridRef = useRef(null);
    const loadingMeetingRecordsRef = useRef(false);
    const loadRequestGenRef = useRef(0); // Generation counter to discard stale API responses
    const loadingStatsRef = useRef(false);
    const loadingRecruitersRef = useRef(false);
    const loadingEventsRef = useRef(false);
    const loadingBoothsRef = useRef(false);
    const selectionUpdateRef = useRef(false); // Prevent multiple simultaneous selection updates
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectAllPages, setSelectAllPages] = useState(false);
    const [allRecordIds, setAllRecordIds] = useState([]);
    // Bulk delete confirmation dialog
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // Job Seeker Profile Modal state
    const [showJobSeekerModal, setShowJobSeekerModal] = useState(false);
    const [selectedJobSeekerForModal, setSelectedJobSeekerForModal] = useState(null);
    const [selectedEventIdForModal, setSelectedEventIdForModal] = useState(null);

    // Meeting Record Detail Modal state
    const [showMeetingDetailModal, setShowMeetingDetailModal] = useState(false);
    const [selectedMeetingRecordId, setSelectedMeetingRecordId] = useState(null);

    const openMeetingDetailModal = useCallback((meetingRecordId) => {
        if (!meetingRecordId) return;
        window.setTimeout(() => {
            setSelectedMeetingRecordId(meetingRecordId);
            setShowMeetingDetailModal(true);
        }, 0);
    }, []);

    const closeMeetingDetailModal = useCallback(() => {
        setShowMeetingDetailModal(false);
        setSelectedMeetingRecordId(null);
    }, []);

    // Resume export state
    const [isExportingResumes, setIsExportingResumes] = useState(false);
    const [activeTab, setActiveTab] = useState('list');
    const supportsMeetingAiTab = ['Admin', 'GlobalSupport', 'Recruiter'].includes(user?.role);

    useEffect(() => {
        if (!supportsMeetingAiTab && activeTab !== 'list') {
            setActiveTab('list');
        }
    }, [supportsMeetingAiTab, activeTab]);

    // Keep the search query and filters in the URL (?search=, ?recruiter=,
    // ?event=, ?booth=, ?status=, ?startDate=, ?endDate=, ?sortBy=, ?sortOrder=)
    // so they survive navigation and reloads without any browser storage.
    const [searchParams, setSearchParams] = useSearchParams();

    // Filters - initialised from the URL query params on mount
    const [filters, setFilters] = useState(() => ({
        recruiterId: searchParams.get('recruiter') || '',
        eventId: searchParams.get('event') || '',
        boothId: searchParams.get('booth') || '',
        status: searchParams.get('status') || '',
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        page: 1,
        limit: 50,
        sortBy: searchParams.get('sortBy') || 'startTime',
        sortOrder: searchParams.get('sortOrder') || 'desc'
    }));

    // Search input ref (uncontrolled to avoid live filtering on typing)
    const searchInputRef = useRef(null);

    // Mirror the filters/search into the URL whenever they change.
    useEffect(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            const setOrDelete = (key, value, defaultValue = '') => {
                const v = (value ?? '').toString().trim();
                if (v && v !== defaultValue) {
                    params.set(key, v);
                } else {
                    params.delete(key);
                }
            };
            setOrDelete('search', filters.search);
            setOrDelete('recruiter', filters.recruiterId);
            setOrDelete('event', filters.eventId);
            setOrDelete('booth', filters.boothId);
            setOrDelete('status', filters.status);
            setOrDelete('startDate', filters.startDate);
            setOrDelete('endDate', filters.endDate);
            setOrDelete('sortBy', filters.sortBy, 'startTime');
            setOrDelete('sortOrder', filters.sortOrder, 'desc');
            return params;
        }, { replace: true });
    }, [filters.search, filters.recruiterId, filters.eventId, filters.boothId, filters.status, filters.startDate, filters.endDate, filters.sortBy, filters.sortOrder, setSearchParams]);

    // Keep the (uncontrolled) search input in sync with the ?search= query param.
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.value = filters.search || '';
        }
    }, [filters.search]);


    // Pagination
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false
    });

    // Frozen columns rely on native Syncfusion movable/frozen pane scrolling.
    useEffect(() => undefined, [meetingRecords]);

    // Statistics
    const [stats, setStats] = useState({
        totalMeetings: 0,
        completedMeetings: 0,
        averageDuration: 0,
        averageRating: 0,
        totalWithRating: 0,
        totalWithInterpreter: 0,
        messagesLeft: 0
    });

    // Syncfusion Toast
    const showToast = (message, type = 'Success', duration = 3000) => {
        if (toastRef.current) {
            toastRef.current.show({
                title: type,
                content: message,
                cssClass: `e-toast-${type.toLowerCase()}`,
                showProgressBar: true,
                timeOut: duration
            });
        }
    };

    // Use ref to store latest filters to avoid recreating callback
    const filtersRef = useRef(filters);
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    const loadMeetingRecords = useCallback(async () => {
        // Track this specific request so stale responses can be discarded
        const gen = ++loadRequestGenRef.current;

        try {
            setLoadingData(true);
            const currentFilters = filtersRef.current;
            console.log('📡 Loading meeting records with filters:', currentFilters);
            const response = await meetingRecordsAPI.getMeetingRecords(currentFilters);
            console.log('✅ Meeting records response:', response);
            
            // Discard results if a newer request has already started
            if (gen !== loadRequestGenRef.current) return;

            // Ensure we have valid data structure
            if (response && response.meetingRecords) {
                console.log(`📊 Loaded ${response.meetingRecords.length} meeting records`);
                setMeetingRecords(response.meetingRecords || []);
                setPagination(response.pagination || {
                    currentPage: 1,
                    totalPages: 0,
                    totalRecords: 0,
                    hasNext: false,
                    hasPrev: false
                });
            } else {
                console.warn('⚠️ Invalid response structure:', response);
                setMeetingRecords([]);
                setPagination({
                    currentPage: 1,
                    totalPages: 0,
                    totalRecords: 0,
                    hasNext: false,
                    hasPrev: false
                });
            }
            // Reset select all pages when data changes
            setSelectAllPages(false);
        } catch (error) {
            console.error('❌ Error loading meeting records:', error);
            console.error('Error details:', {
                message: error.message,
                code: error.code,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config?.url
            });
            
            // Check if it's a CORS error
            const isCorsError = error.code === 'ERR_NETWORK' || 
                               (error.message && error.message.includes('CORS')) ||
                               (error.message && error.message === 'Network Error');
            
            if (isCorsError) {
                showToast('CORS Error: Check server configuration or use proxy', 'Error');
            } else {
                showToast(error.response?.data?.message || 'Failed to load meeting records', 'Error');
            }
            
            // Set empty data on error to prevent UI crashes
            setMeetingRecords([]);
            setPagination({
                currentPage: 1,
                totalPages: 0,
                totalRecords: 0,
                hasNext: false,
                hasPrev: false
            });
        } finally {
            if (gen === loadRequestGenRef.current) {
                setLoadingData(false);
            }
        }
    }, []); // No dependencies - uses ref for filters

    const loadStats = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (loadingStatsRef.current) return;
        
        try {
            loadingStatsRef.current = true;
            const currentFilters = filtersRef.current;
            console.log('📊 Loading stats with filters:', currentFilters);
            const statsData = await meetingRecordsAPI.getStats(currentFilters);
            console.log('✅ Stats data received:', statsData);
            setStats({
                totalMeetings: statsData.totalMeetings || 0,
                completedMeetings: statsData.completedMeetings || 0,
                averageDuration: statsData.averageDuration || null,
                averageRating: statsData.averageRating || null,
                totalWithRating: statsData.totalWithRating || 0,
                totalWithInterpreter: statsData.totalWithInterpreter || 0,
                messagesLeft: statsData.messagesLeft || 0
            });
        } catch (error) {
            console.error('❌ Error loading stats:', error);
            console.error('Error details:', error.response?.data || error.message);
            // Set default stats on error
            setStats({
                totalMeetings: 0,
                completedMeetings: 0,
                averageDuration: null,
                averageRating: null,
                totalWithRating: 0,
                totalWithInterpreter: 0,
                messagesLeft: 0
            });
        } finally {
            loadingStatsRef.current = false;
        }
    }, []); // No dependencies - uses ref for filters

    const loadRecruiters = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (loadingRecruitersRef.current) return;
        
        try {
            loadingRecruitersRef.current = true;
            if (['Admin', 'GlobalSupport'].includes(user?.role)) {
                const response = await listUsers({ role: 'Recruiter', limit: 1000 });
                setRecruiters(response.users || []);
            }
        } catch (error) {
            console.error('Error loading recruiters:', error);
        } finally {
            loadingRecruitersRef.current = false;
        }
    }, [user?.role]);

    const loadEvents = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (loadingEventsRef.current) return;
        
        try {
            loadingEventsRef.current = true;
            const response = await listEvents({ limit: 1000 });
            setEvents(response.events || []);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            loadingEventsRef.current = false;
        }
    }, []);

    const loadBooths = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (loadingBoothsRef.current) return;
        
        try {
            loadingBoothsRef.current = true;
            const response = await listBooths({ limit: 1000 });
            setBooths(response.booths || []);
        } catch (error) {
            console.error('Error loading booths:', error);
        } finally {
            loadingBoothsRef.current = false;
        }
    }, []);

    // Load data when user is available or filters change
    useEffect(() => {
        if (user) {
            loadMeetingRecords();
            loadStats();
            loadRecruiters();
            loadEvents();
            loadBooths();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, location.key]);

    // Reload meeting records when filters change.
    // Debounce so rapid Search/Clear collapse into a single request; the stale-response
    // guard in loadMeetingRecords discards any overlap.
    useEffect(() => {
        if (!user) return;
        const t = setTimeout(() => { loadMeetingRecords(); }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filters.eventId, filters.boothId, filters.recruiterId, filters.status, filters.startDate, filters.endDate, filters.search, filters.page, filters.limit]);

    // Reload stats when filters change (excluding page and limit which don't affect stats)
    useEffect(() => {
        if (user) {
            loadStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filters.eventId, filters.boothId, filters.recruiterId, filters.status, filters.startDate, filters.endDate, filters.search]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (newSize) => {
        setFilters(prev => ({ ...prev, limit: newSize, page: 1 }));
    };

    // Helper function to escape CSV fields
    const escapeCSV = (value) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    // Helper function to format date consistently
    const formatDateForCSV = (date) => {
        if (!date) return '';
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return '';
            // Format: YYYY-MM-DD HH:MM:SS
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch (e) {
            return '';
        }
    };

    // Helper function to format duration
    const formatDurationForCSV = (duration, startTime, endTime) => {
        if (duration !== null && duration !== undefined && duration !== '') {
            return String(duration);
        }
        if (startTime && endTime) {
            try {
                const start = new Date(startTime);
                const end = new Date(endTime);
                if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start) {
                    return String(Math.round((end - start) / (1000 * 60)));
                }
            } catch (e) {
                // Ignore
            }
        }
        return '';
    };

    const handleExport = async () => {
        try {
            console.log('📤 Starting export...');
            setLoadingData(true);
            
            // Check for selected records first
            const currentSelection = getSelectedRecordsFromGrid();
            
            if (currentSelection.length > 0) {
                // Export selected records only (client-side)
                console.log(`📊 Exporting ${currentSelection.length} selected record(s)`);
                const selectedIds = currentSelection.map(id => String(id));
                const selectedData = meetingRecords.filter(r => {
                    const recordId = String(r._id || r.id || '');
                    return selectedIds.includes(recordId);
                });
                
                if (selectedData.length > 0) {
                    exportToCSVClientSide(selectedData);
                    showToast(`✅ Exported ${selectedData.length} selected record(s)`, 'Success');
                } else {
                    showToast('No matching data found for selected records', 'Warning');
                }
                setLoadingData(false);
                return;
            }
            
            // Check if there are column filters applied in the grid
            let hasColumnFilters = false;
            if (gridRef.current) {
                try {
                    // Check if grid has any active column filters
                    const gridInstance = gridRef.current;
                    if (gridInstance.filterSettings && gridInstance.filterSettings.columns && gridInstance.filterSettings.columns.length > 0) {
                        hasColumnFilters = true;
                        console.log('📊 Column filters detected in grid');
                    }
                } catch (e) {
                    console.warn('Could not check grid filters:', e);
                }
            }
            
            // If column filters are applied, we need to fetch all data and filter client-side
            // Otherwise, use server-side export API which is more efficient
            if (hasColumnFilters) {
                // Fetch ALL records matching current server-side filters (not paginated)
                const exportFilters = {
                    ...filters,
                    page: 1,
                    limit: 100000 // Large limit to get all records
                };
                
                console.log('📡 Fetching ALL records for export with server-side filters:', exportFilters);
                const response = await meetingRecordsAPI.getMeetingRecords(exportFilters);
                let allRecords = response.meetingRecords || [];
                
                console.log(`📊 Fetched ${allRecords.length} total records from server`);
                
                // Map records to match grid dataSource structure
                const mappedRecords = allRecords.map(r => {
                    const getFieldName = (field) => {
                        if (!field) return '';
                        if (typeof field === 'string') return field;
                        if (typeof field === 'object' && field.name) return field.name;
                        return '';
                    };
                    
                    const getFieldEmail = (field) => {
                        if (!field) return '';
                        if (typeof field === 'object' && field.email) return field.email;
                        return '';
                    };
                    
                    const getFieldResumeUrl = (field) => {
                        if (!field) return '';
                        if (typeof field === 'object' && field.resumeUrl) return field.resumeUrl;
                        return '';
                    };
                    
                    const getFieldCity = (field) => {
                        if (!field) return '';
                        if (typeof field === 'object' && field.city) return field.city;
                        return '';
                    };
                    
                    return {
                        ...r,
                        id: r._id || r.id,
                        eventName: getFieldName(r.eventId),
                        boothName: getFieldName(r.boothId),
                        recruiterName: getFieldName(r.recruiterId),
                        appliedPosition: getAppliedPosition(r),
                        appliedLocation: getAppliedLocation(r),
                        jobSeekerName: getFieldName(r.jobseekerId),
                        jobSeekerEmail: getFieldEmail(r.jobseekerId),
                        jobSeekerResumeId: r.jobSeekerResumeId || r.resolvedResume?.resumeId || '',
                        jobSeekerResumeUrl: r.jobSeekerResumeUrl || r.registeredResumeUrl || r.resolvedResume?.resumeUrl || getFieldResumeUrl(r.jobseekerId),
                        jobSeekerCity: getFieldCity(r.jobseekerId),
                        interpreterName: r.interpreterId ? getFieldName(r.interpreterId) : 'None',
                        statusLabel: STATUS_LABELS[r.status] || r.status || ''
                    };
                });
                
                // Apply column filters by temporarily updating grid dataSource
                let recordsToExport = mappedRecords;
                try {
                    // Save current state
                    const originalDataSource = gridRef.current.dataSource;
                    const originalPageSettings = gridRef.current.pageSettings;
                    
                    // Temporarily set all records and disable paging
                    gridRef.current.dataSource = mappedRecords;
                    gridRef.current.pageSettings = { pageSize: mappedRecords.length };
                    gridRef.current.refresh();
                    
                    // Wait for grid to process
                    await new Promise(resolve => setTimeout(resolve, 200));
                    
                    // Get filtered records
                    const filteredRows = gridRef.current.getFilteredRecords();
                    
                    // Restore original state
                    gridRef.current.dataSource = originalDataSource;
                    gridRef.current.pageSettings = originalPageSettings;
                    gridRef.current.refresh();
                    
                    if (filteredRows && filteredRows.length > 0) {
                        const filteredIds = new Set(filteredRows.map(row => (row._id || row.id)));
                        recordsToExport = mappedRecords.filter(r => filteredIds.has(r._id || r.id));
                        console.log(`📊 Applied column filters: ${recordsToExport.length} records (from ${mappedRecords.length} total)`);
                    }
                } catch (gridError) {
                    console.warn('⚠️ Could not apply column filters, exporting all:', gridError);
                }
                
                // Export using client-side CSV generation
                exportToCSVClientSide(recordsToExport);
            } else {
                // No column filters - use efficient server-side export
                console.log('📡 Using server-side export API (no column filters)');
                const exportFilters = {
                    ...filters
                    // Remove pagination params for export
                };
                delete exportFilters.page;
                delete exportFilters.limit;
                
                const blob = await meetingRecordsAPI.exportCSV(exportFilters);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = 'meeting-records.csv';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                const message = filters.search 
                    ? `Exported search results successfully` 
                    : 'Meeting records exported successfully';
                showToast(message, 'Success');
            }
        } catch (error) {
            console.error('Error exporting meeting records:', error);
            showToast('Failed to export meeting records', 'Error');
        } finally {
            setLoadingData(false);
        }
    };
    
    // Helper: value -> label for dropdown fields in CSV
    const getLabelFromValue = (value, optionsList) => {
        if (value === null || value === undefined || value === '') return '';
        const s = String(value).trim();
        const opt = optionsList.find(o => (o.value || '').toString() === s || (o.name || '').toString() === s);
        return opt ? (opt.name || opt.value || s) : s;
    };
    const getLabelFromValues = (values, optionsList) => {
        if (!values || !Array.isArray(values)) return '';
        return values.map(v => getLabelFromValue(v, optionsList)).filter(Boolean).join('; ');
    };

    // Helper function for client-side CSV export
    const exportToCSVClientSide = (recordsToExport) => {
        // CSV Headers (no "Job Seeker" prefix)
        const csvHeaders = [
            'Event Name',
            'Booth',
            'Position',
            'Position Location',
            'Recruiter Name',
            'Recruiter Email',
            'First Name',
            'Last Name',
            'Email',
            'Phone',
            'Location',
            'Headline',
            'Keywords',
            'Primary Job Experience',
            'Work Experience Level',
            'Highest Education Level',
            'Employment Types',
            'Language(s)',
            'Veteran/Military Status',
            'Resume Link',
            'Interpreter',
            'Start Time',
            'End Time',
            'Duration (minutes)',
            'Status',
            'Rating',
            'Feedback',
            'Messages Count'
        ];

        // Convert records to CSV rows
        const csvRows = recordsToExport.map(record => {
                // Helper functions to extract data
                const getFieldName = (field) => {
                    if (!field) return '';
                    if (typeof field === 'string') return field;
                    if (typeof field === 'object' && field.name) return field.name;
                    return '';
                };
                
                const getFieldEmail = (field) => {
                    if (!field) return '';
                    if (typeof field === 'string') return field;
                    if (typeof field === 'object' && field.email) return String(field.email).trim();
                    return '';
                };
                
                const getFieldResumeUrl = (field) => {
                    if (!field) return '';
                    // Check if already extracted (from mapping above)
                    if (typeof field === 'string' && field.startsWith('http')) return field;
                    if (typeof field === 'object' && field.resumeUrl) return String(field.resumeUrl).trim();
                    // Also check the original nested structure
                    if (record.jobseekerId && typeof record.jobseekerId === 'object') {
                        if (record.jobseekerId.resumeUrl) return String(record.jobseekerId.resumeUrl).trim();
                    }
                    return '';
                };
                
                const getFieldCity = (field) => {
                    if (!field) return '';
                    if (typeof field === 'string') return field;
                    if (typeof field === 'object' && field.city) return String(field.city).trim();
                    return '';
                };
                
                const getFieldState = (field) => {
                    if (!field) return '';
                    if (typeof field === 'string') return field;
                    if (typeof field === 'object' && field.state) return String(field.state).trim();
                    return '';
                };

                // Extract values - use mapped fields if available, otherwise extract from nested objects
                const eventName = record.eventName || getFieldName(record.eventId);
                const boothName = record.boothName || getFieldName(record.boothId);
                const recruiterName = record.recruiterName || getFieldName(record.recruiterId);
                const recruiterEmail = getFieldEmail(record.recruiterId);
                const jobSeekerName = record.jobSeekerName || getFieldName(record.jobseekerId);
                
                // Split name into first and last name
                const nameParts = jobSeekerName ? jobSeekerName.split(/\s+/) : [];
                const jobSeekerFirstName = nameParts[0] || '';
                const jobSeekerLastName = nameParts.slice(1).join(' ') || '';
                
                const jobSeekerEmail = record.jobSeekerEmail || getFieldEmail(record.jobseekerId);
                
                // Extract phone number
                const jobSeekerPhone = (record.jobseekerId && typeof record.jobseekerId === 'object' && record.jobseekerId.phoneNumber) 
                    ? String(record.jobseekerId.phoneNumber).trim() 
                    : '';
                
                const jobSeekerResumeUrl = record.jobSeekerResumeUrl || getFieldResumeUrl(record.jobseekerId);
                const jobSeekerCity = record.jobSeekerCity || getFieldCity(record.jobseekerId);
                const jobSeekerState = getFieldState(record.jobseekerId);
                
                let location = '';
                if (jobSeekerCity && jobSeekerState) {
                    location = `${jobSeekerCity}, ${jobSeekerState}`;
                } else if (jobSeekerCity) {
                    location = jobSeekerCity;
                } else if (jobSeekerState) {
                    location = jobSeekerState;
                }
                
                // Extract profile data from metadata and convert to labels
                const jobSeeker = record.jobseekerId;
                const profile = (jobSeeker && jobSeeker.metadata && jobSeeker.metadata.profile) ? jobSeeker.metadata.profile : null;
                const headline = profile && profile.headline ? String(profile.headline).trim() : '';
                const keywords = profile && profile.keywords ? String(profile.keywords).trim() : '';
                const primaryJobExperience = Array.isArray(profile?.primaryExperience)
                    ? profile.primaryExperience.find(Boolean) || ''
                    : profile?.primaryExperience || '';
                const primaryJobExperienceLabel = getLabelFromValue(primaryJobExperience, JOB_CATEGORY_LIST);
                const workLevelLabel = getLabelFromValue(profile?.workLevel || '', EXPERIENCE_LEVEL_LIST);
                const educationLevelLabel = getLabelFromValue(profile?.educationLevel || '', EDUCATION_LEVEL_LIST);
                const employmentTypesLabel = getLabelFromValues(profile?.employmentTypes || [], JOB_TYPE_LIST);
                const languagesDisplay = getLabelFromValues(profile?.languages || [], LANGUAGE_LIST);
                const veteranStatusLabel = getLabelFromValue(profile?.veteranStatus || '', MILITARY_EXPERIENCE_LIST);
                
                const interpreterName = (record.interpreterId && typeof record.interpreterId === 'object' && record.interpreterId.name) 
                    ? record.interpreterId.name 
                    : 'None';
                
                const duration = formatDurationForCSV(record.duration, record.startTime, record.endTime);
                const status = STATUS_LABELS[record.status] || record.status || '';
                const rating = (record.recruiterRating !== null && record.recruiterRating !== undefined && record.recruiterRating !== '') 
                    ? String(record.recruiterRating) 
                    : '';
                const feedback = (record.recruiterFeedback !== null && record.recruiterFeedback !== undefined) 
                    ? String(record.recruiterFeedback).trim() 
                    : '';
                const messagesCount = Array.isArray(record.jobSeekerMessages) 
                    ? String(record.jobSeekerMessages.length) 
                    : '0';

                return [
                    escapeCSV(eventName),
                    escapeCSV(boothName),
                    escapeCSV(getAppliedPosition(record)),
                    escapeCSV(getAppliedLocation(record)),
                    escapeCSV(recruiterName),
                    escapeCSV(recruiterEmail),
                    escapeCSV(jobSeekerFirstName),
                    escapeCSV(jobSeekerLastName),
                    escapeCSV(jobSeekerEmail),
                    escapeCSV(jobSeekerPhone),
                    escapeCSV(location),
                    escapeCSV(headline),
                    escapeCSV(keywords),
                    escapeCSV(primaryJobExperienceLabel),
                    escapeCSV(workLevelLabel),
                    escapeCSV(educationLevelLabel),
                    escapeCSV(employmentTypesLabel),
                    escapeCSV(languagesDisplay),
                    escapeCSV(veteranStatusLabel),
                    escapeCSV(jobSeekerResumeUrl),
                    escapeCSV(interpreterName),
                    escapeCSV(formatDateForCSV(record.startTime)),
                    escapeCSV(formatDateForCSV(record.endTime)),
                    escapeCSV(duration),
                    escapeCSV(status),
                    escapeCSV(rating),
                    escapeCSV(feedback),
                    escapeCSV(messagesCount)
                ];
        });

        // Build CSV content
        const csvContent = [
            csvHeaders.map(h => escapeCSV(h)).join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\r\n');

        // Add BOM for Excel compatibility (UTF-8 BOM)
        const BOM = '\uFEFF';
        const finalContent = BOM + csvContent;

        // Create blob and download
        const blob = new Blob([finalContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'meeting-records.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        showToast(`Exported ${recordsToExport.length} meeting record(s) successfully`, 'Success');
    };

    const handleExportResumes = async () => {
        try {
            setIsExportingResumes(true);
            
            // Helper function to get file extension from URL
            const getFileExtension = (url) => {
                if (!url) return 'pdf';
                const match = url.match(/\.([a-zA-Z0-9]+)(\?|$)/);
                return match ? match[1].toLowerCase() : 'pdf';
            };

            // Helper function to sanitize filename
            const sanitizeFileName = (name) => {
                return name.replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 100);
            };

            // Get selected records from grid
            const selectedFromGrid = getSelectedRecordsFromGrid();
            let selectedIds = null;

            // Determine which records to process
            if (selectedFromGrid.length > 0) {
                selectedIds = selectedFromGrid;
                showToast(`Fetching resumes for ${selectedFromGrid.length} selected job seeker(s)...`, 'Info', 2000);
            } else if (selectedRecords.length > 0) {
                selectedIds = selectedRecords;
                showToast(`Fetching resumes for ${selectedRecords.length} selected job seeker(s)...`, 'Info', 2000);
            } else {
                showToast('No records selected. Fetching all job seekers matching current filters...', 'Info', 2000);
            }

            // Call the API endpoint to get job seekers with proper filtering
            const response = await meetingRecordsAPI.getJobSeekersForResumeExport(filters, selectedIds);
            const uniqueJobSeekers = response.jobSeekers || [];

            if (uniqueJobSeekers.length === 0) {
                showToast('No job seekers with resume URLs found', 'Warning');
                return;
            }

            showToast(`Downloading ${uniqueJobSeekers.length} resume(s)...`, 'Info', 3000);

            // Create zip file
            const zip = new JSZip();

            // Download each resume and add to zip
            let successCount = 0;
            let failCount = 0;
            const errors = [];

            for (let i = 0; i < uniqueJobSeekers.length; i++) {
                const jobSeeker = uniqueJobSeekers[i];
                try {
                    // Fetch the resume file through proxy endpoint to avoid CORS issues
                    const token = localStorage.getItem('token');
                    const proxyUrl = `/api/uploads/proxy/download?url=${encodeURIComponent(jobSeeker.resumeUrl)}`;
                    
                    const response = await fetch(proxyUrl, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }

                    const blob = await response.blob();
                    
                    // Validate blob - check if it's not empty
                    if (!blob || blob.size === 0) {
                        throw new Error('Downloaded file is empty');
                    }
                    
                    // Get file extension
                    const extension = getFileExtension(jobSeeker.resumeUrl);
                    const sanitizedName = sanitizeFileName(jobSeeker.name);
                    const fileName = `${sanitizedName}_${jobSeeker.id}.${extension}`;

                    // Add to zip
                    zip.file(fileName, blob);
                    successCount++;
                    console.log(`✅ Downloaded resume ${i + 1}/${uniqueJobSeekers.length}: ${fileName} (${blob.size} bytes)`);

                    // Update progress
                    if ((i + 1) % 10 === 0 || i === uniqueJobSeekers.length - 1) {
                        showToast(`Downloaded ${i + 1}/${uniqueJobSeekers.length} resumes...`, 'Info', 2000);
                    }
                } catch (error) {
                    console.error(`❌ Error downloading resume for ${jobSeeker.name} (${jobSeeker.id}):`, error);
                    console.error(`   Resume URL: ${jobSeeker.resumeUrl}`);
                    console.error(`   Error details:`, error.message, error.stack);
                    failCount++;
                    errors.push(`${jobSeeker.name}: ${error.message}`);
                }
            }

            if (successCount === 0) {
                showToast('Failed to download any resumes. Please check the resume URLs.', 'Error', 5000);
                return;
            }

            // Generate zip file
            showToast('Creating zip file...', 'Info', 2000);
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Download the zip file
            const url = window.URL.createObjectURL(zipBlob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            
            // Generate filename based on filters
            let filename = 'job-seeker-resumes';
            const selectedCount = selectedFromGrid.length > 0 ? selectedFromGrid.length : selectedRecords.length;
            
            if (selectedCount > 0) {
                filename = `selected-${selectedCount}-resumes`;
            } else if (filters.eventId) {
                const event = events.find(e => e._id === filters.eventId);
                if (event) {
                    filename = `${sanitizeFileName(event.name)}-resumes`;
                }
            }
            filename += '.zip';
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Show success message with summary
            let message = `Successfully exported ${successCount} resume(s)`;
            if (failCount > 0) {
                message += `. ${failCount} resume(s) failed to download.`;
            }
            showToast(message, successCount > 0 ? 'Success' : 'Warning', 5000);

            if (errors.length > 0 && errors.length <= 5) {
                console.warn('Resume download errors:', errors);
            }
        } catch (error) {
            console.error('Error exporting resumes:', error);
            showToast(`Failed to export resumes: ${error.message}`, 'Error', 5000);
        } finally {
            setIsExportingResumes(false);
        }
    };

    const clearFilters = () => {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setFilters({
            recruiterId: '',
            eventId: '',
            boothId: '',
            status: '',
            startDate: '',
            endDate: '',
            search: '',
            page: 1,
            limit: 50,
            sortBy: 'startTime',
            sortOrder: 'desc'
        });
    };
    
    const handleSearch = () => {
        const query = (searchInputRef.current?.value || '').trim();
        setFilters(prev => ({
            ...prev,
            search: query,
            page: 1 // Reset to first page when searching
        }));
    };
    
    const handleClearSearch = () => {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setFilters(prev => ({
            ...prev,
            search: '',
            page: 1
        }));
    };

    const formatDuration = (minutes) => {
        if (!minutes || minutes === 0 || isNaN(minutes)) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const renderStars = (rating) => {
        if (!rating) return 'No rating';
        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
    };

    const handleSelectAll = () => {
        if (gridRef.current) {
            gridRef.current.selectRows(Array.from({ length: gridRef.current.currentViewData.length }, (_, i) => i));
        }
    };

    const handleDeselectAll = () => {
        if (gridRef.current) {
            gridRef.current.clearSelection();
        }
        setSelectedRecords([]);
        setSelectAllPages(false);
    };

    const handleSelectAllPages = async () => {
        try {
            // Fetch all record IDs (without pagination)
            const allFilters = { ...filters, page: 1, limit: 99999 };
            const response = await meetingRecordsAPI.getMeetingRecords(allFilters);
            const allIds = response.meetingRecords.map(r => r._id);
            setAllRecordIds(allIds);
            setSelectedRecords(allIds);
            setSelectAllPages(true);
            showToast(`Selected all ${allIds.length} records across all pages`, 'Info');
        } catch (error) {
            console.error('Error fetching all records:', error);
            showToast('Failed to select all records', 'Error');
        }
    };

    const handleSelectRecord = useCallback((recordId) => {
        setSelectedRecords(prev => {
            if (prev.includes(recordId)) {
                return prev.filter(id => id !== recordId);
            } else {
                return [...prev, recordId];
            }
        });
    }, []);

    const handleRowSelected = useCallback(() => {
        // Prevent multiple simultaneous updates
        if (selectionUpdateRef.current) return;
        
        selectionUpdateRef.current = true;
        
        // Use requestAnimationFrame to batch updates and avoid blocking the UI
        requestAnimationFrame(() => {
            if (gridRef.current) {
                try {
                    const selectedRecords = gridRef.current.getSelectedRecords();
                    const selectedIds = selectedRecords.map(record => record.id || record._id);
                    setSelectedRecords(selectedIds);
                } catch (error) {
                    console.warn('Error getting selected records:', error);
                }
            }
            selectionUpdateRef.current = false;
        });
    }, []);

    // Memoize the dataSource transformation to prevent unnecessary re-renders
    const gridDataSource = useMemo(() => {
        // Safely extract populated field values
        // Backend populates these fields, so they can be objects or null
        const getFieldName = (field) => {
            if (!field) return '';
            if (typeof field === 'string') return field;
            if (typeof field === 'object' && field.name) return field.name;
            return '';
        };
        
        const getFieldCity = (field) => {
            if (!field) return '';
            if (typeof field === 'object' && field.city) return field.city;
            return '';
        };
        
        const getFieldEmail = (field) => {
            if (!field) return '';
            if (typeof field === 'object' && field.email) return field.email;
            return '';
        };
        
        const getFieldResumeUrl = (field) => {
            if (!field) return '';
            if (typeof field === 'object' && field.resumeUrl) return field.resumeUrl;
            return '';
        };
        
        // Helper to get profile from job seeker metadata
        const getProfile = (jobSeeker) => {
            if (!jobSeeker || typeof jobSeeker !== 'object') return null;
            
            // Handle metadata.profile structure
            if (jobSeeker.metadata) {
                // If metadata is an object with profile property
                if (typeof jobSeeker.metadata === 'object' && jobSeeker.metadata.profile) {
                    return jobSeeker.metadata.profile;
                }
                // If metadata is a JSON string
                if (typeof jobSeeker.metadata === 'string') {
                    try {
                        const parsed = JSON.parse(jobSeeker.metadata);
                        return parsed?.profile || null;
                    } catch (e) {
                        return null;
                    }
                }
            }
            return null;
        };

        const getLabelsArrayFromValues = (values, optionsList) => {
            if (!Array.isArray(values)) return [];
            return values
                .map((value) => getLabelFromValue(value, optionsList))
                .filter((value) => value && value !== 'Not provided');
        };

        // Helper to split name
        const splitName = (name) => {
            if (!name) return { firstName: '', lastName: '' };
            const parts = name.trim().split(/\s+/);
            return {
                firstName: parts[0] || '',
                lastName: parts.slice(1).join(' ') || ''
            };
        };
        
        return meetingRecords.map(r => {
            const jobSeeker = r.jobseekerId;
            const name = getFieldName(jobSeeker);
            const nameParts = splitName(name);
            const profile = getProfile(jobSeeker);

            return {
            ...r, 
            id: r._id || r.id,
            // Flatten nested fields for sorting and filtering
            eventName: getFieldName(r.eventId),
            boothName: getFieldName(r.boothId),
            recruiterName: getFieldName(r.recruiterId),
            appliedPosition: getAppliedPosition(r),
            appliedLocation: getAppliedLocation(r),
                jobSeekerName: name,
                jobSeekerFirstName: nameParts.firstName,
                jobSeekerLastName: nameParts.lastName,
                jobSeekerAvatarUrl: (jobSeeker && typeof jobSeeker === 'object' && jobSeeker.avatarUrl) ? jobSeeker.avatarUrl : '',
                jobSeekerEmail: getFieldEmail(jobSeeker),
                jobSeekerPhone: (jobSeeker && typeof jobSeeker === 'object' && jobSeeker.phoneNumber) ? jobSeeker.phoneNumber : '',
                jobSeekerCity: getFieldCity(jobSeeker),
                jobSeekerState: (jobSeeker && typeof jobSeeker === 'object' && jobSeeker.state) ? jobSeeker.state : '',
                jobSeekerCountry: (jobSeeker && typeof jobSeeker === 'object' && jobSeeker.country) ? jobSeeker.country : '',
                jobSeekerHeadline: profile?.headline || '',
                jobSeekerKeywords: profile?.keywords || '',
                jobSeekerPrimaryExperience: getLabelFromValue(
                    Array.isArray(profile?.primaryExperience)
                        ? profile.primaryExperience.find(Boolean) || ''
                        : profile?.primaryExperience || '',
                    JOB_CATEGORY_LIST
                ) === 'Not provided'
                    ? ''
                    : getLabelFromValue(
                        Array.isArray(profile?.primaryExperience)
                            ? profile.primaryExperience.find(Boolean) || ''
                            : profile?.primaryExperience || '',
                        JOB_CATEGORY_LIST
                    ),
                jobSeekerWorkLevel: getLabelFromValue(profile?.workLevel || '', EXPERIENCE_LEVEL_LIST) === 'Not provided'
                    ? ''
                    : getLabelFromValue(profile?.workLevel || '', EXPERIENCE_LEVEL_LIST),
                jobSeekerEducationLevel: getLabelFromValue(profile?.educationLevel || '', EDUCATION_LEVEL_LIST) === 'Not provided'
                    ? ''
                    : getLabelFromValue(profile?.educationLevel || '', EDUCATION_LEVEL_LIST),
                jobSeekerEmploymentTypes: labelsToFilterText(getLabelsArrayFromValues(profile?.employmentTypes || [], JOB_TYPE_LIST)),
                jobSeekerLanguages: labelsToFilterText(getLabelsArrayFromValues(profile?.languages || [], LANGUAGE_LIST)),
                jobSeekerVeteranStatus: getLabelFromValue(profile?.veteranStatus || '', MILITARY_EXPERIENCE_LIST) === 'Not provided'
                    ? ''
                    : getLabelFromValue(profile?.veteranStatus || '', MILITARY_EXPERIENCE_LIST),
                jobSeekerResumeId: r.jobSeekerResumeId || r.resolvedResume?.resumeId || '',
                jobSeekerResumeUrl: r.jobSeekerResumeUrl || r.registeredResumeUrl || r.resolvedResume?.resumeUrl || getFieldResumeUrl(jobSeeker),
            interpreterName: r.interpreterId ? getFieldName(r.interpreterId) : 'None',
            statusLabel: STATUS_LABELS[r.status] || r.status || ''
            };
        });
    }, [meetingRecords]);

    // Syncfusion Grid does not reliably pick up dataSource prop changes. Calling
    // refresh() alone only re-renders the grid's *existing* internal dataSource, so
    // searched/filtered results only appeared after a full page reload remounted the
    // grid. Assigning the new array to the grid instance forces EJ2 to rebind to the
    // fresh data (which also re-renders templated cells).
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;
        grid.dataSource = gridDataSource;
    }, [gridDataSource]);

    // Memoize grid settings to prevent unnecessary re-renders
    const gridFilterSettings = useMemo(() => SYNC_GRID_FILTER_SETTINGS, []);

    const gridToolbar = useMemo(() => ['ColumnChooser'], []);

    const gridSelectionSettings = useMemo(() => ({
        type: 'Multiple',
        checkboxOnly: true
    }), []);

    // Get selected records from grid when needed (for delete, export, etc.)
    const getSelectedRecordsFromGrid = useCallback(() => {
        if (!gridRef.current) return [];
        
        try {
            // Use getSelectedRecords - most reliable method for Syncfusion Grid
            if (typeof gridRef.current.getSelectedRecords === 'function') {
                const rows = gridRef.current.getSelectedRecords();
                // Return string IDs for consistent comparison
                return rows.map(row => String(row.id || row._id || '')).filter(Boolean);
            }
            return [];
        } catch (error) {
            console.error('Error getting selected rows:', error);
            return [];
        }
    }, []);

    // Use ref to track selection update timeout
    const selectionUpdateTimeoutRef = useRef(null);

    const handleBulkDelete = () => {
        // Get fresh selection from grid
        const currentSelection = getSelectedRecordsFromGrid();
        if (currentSelection.length === 0) {
            showToast('Please select records to delete', 'Warning');
            return;
        }
        setSelectedRecords(currentSelection);
        setConfirmDeleteOpen(true);
    };

    const confirmBulkDelete = async () => {
        try {
            setIsDeleting(true);
            const response = await meetingRecordsAPI.bulkDelete(selectedRecords);
            showToast(response.message || 'Records deleted successfully', 'Success');
            setSelectedRecords([]);
            setSelectAllPages(false);
            await loadMeetingRecords();
            await loadStats();
        } catch (error) {
            console.error('Error deleting records:', error);
            showToast(error.response?.data?.message || 'Failed to delete records', 'Error');
        } finally {
            setIsDeleting(false);
            setConfirmDeleteOpen(false);
        }
    };

    const cancelBulkDelete = () => {
        setConfirmDeleteOpen(false);
    };

    // Grid template functions for custom column renders - memoized to prevent re-renders
    const avatarTemplate = useCallback((props) => jobSeekerAvatarCellTemplate(props), []);

    const eventTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.eventId?.name || 'N/A'}
            </div>
        );
    }, []);

    const boothTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center' }}>
                {row.boothId?.name || 'N/A'}
            </div>
        );
    }, []);

    const recruiterTemplate = useCallback((props) => {
        const row = props;
        // For left_with_message records, show "All Recruiters in Booth" instead of specific recruiter
        if (row.status === 'left_with_message') {
            return (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ fontStyle: 'italic', color: '#6b7280' }}>All Recruiters in Booth</span>
                </div>
            );
        }
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.recruiterId?.name || 'N/A'}
            </div>
        );
    }, []);

    const jobSeekerTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.jobseekerId?.name || 'N/A'}
            </div>
        );
    }, []);

    const jobSeekerEmailTemplate = useCallback((props) => {
        const row = props;
        const email = row.jobseekerId?.email || row.jobSeekerEmail || 'N/A';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {email !== 'N/A' ? (
                    <a 
                        href={`mailto:${email}`}
                        style={{ color: '#111827', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                        title={`Send email to ${email}`}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {email}
                    </a>
                ) : (
                    <span style={{ color: '#111827' }}>N/A</span>
                )}
            </div>
        );
    }, []);

    const locationTemplate = useCallback((props) => {
        const row = props;
        const jobSeeker = row.jobseekerId;
        let locationText = 'N/A';
        if (jobSeeker?.city && jobSeeker?.state) {
            locationText = `${jobSeeker.city}, ${jobSeeker.state}`;
        }
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {locationText}
            </div>
        );
    }, []);

    const startTimeTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {formatDateTime(row.startTime)}
            </div>
        );
    }, []);

    const durationTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {formatDuration(row.duration)}
            </div>
        );
    }, []);

    const statusTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <span className={`status-badge status-${row.status}`}>
                    {row.statusLabel || STATUS_LABELS[row.status] || row.status}
                </span>
            </div>
        );
    }, []);

    const ratingTemplate = useCallback((props) => {
        const row = props;
        return (
            <div className="rating-display" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <span className="stars">{renderStars(row.recruiterRating)}</span>
                {row.recruiterRating && (
                    <span className="rating-number">({row.recruiterRating}/5)</span>
                )}
            </div>
        );
    }, []);

    const interpreterTemplate = useCallback((props) => {
        const row = props;
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {row.interpreterId?.name || 'None'}
            </div>
        );
    }, []);

    const meetingNotesTemplate = useCallback((props) => {
        const row = props;
        const notes = row.recruiterFeedback || '';
        const truncatedNotes = notes.length > 100 ? notes.substring(0, 100) + '...' : notes;
        return (
            <div 
                style={{ 
                    wordWrap: 'break-word', 
                    whiteSpace: 'normal', 
                    padding: '8px 0',
                    maxWidth: '300px',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
                title={notes || 'No notes'}
            >
                {notes ? truncatedNotes : 'N/A'}
            </div>
        );
    }, []);

    const phoneTemplate = useCallback((props) => {
        const row = props;
        const phone = row.jobSeekerPhone || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {phone || 'N/A'}
            </div>
        );
    }, []);

    const appliedPositionTemplate = useCallback((props) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {props.appliedPosition || 'N/A'}
        </div>
    ), []);

    const appliedLocationTemplate = useCallback((props) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {props.appliedLocation || 'N/A'}
        </div>
    ), []);

    const headlineTemplate = useCallback((props) => {
        const row = props;
        const headline = row.jobSeekerHeadline || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {headline || 'N/A'}
            </div>
        );
    }, []);

    const keywordsTemplate = useCallback((props) => {
        const row = props;
        const keywords = row.jobSeekerKeywords || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {keywords || 'N/A'}
            </div>
        );
    }, []);

    const primaryJobExperienceTemplate = useCallback((props) => {
        const row = props;
        const primaryExperience = row.jobSeekerPrimaryExperience || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {primaryExperience || 'N/A'}
            </div>
        );
    }, []);

    const workExperienceLevelTemplate = useCallback((props) => {
        const row = props;
        const workLevel = row.jobSeekerWorkLevel || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {workLevel || 'N/A'}
            </div>
        );
    }, []);

    const educationLevelTemplate = useCallback((props) => {
        const row = props;
        const educationLevel = row.jobSeekerEducationLevel || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {educationLevel || 'N/A'}
            </div>
        );
    }, []);

    const employmentTypesTemplate = useCallback((props) => {
        const displayValue = props.jobSeekerEmploymentTypes || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {displayValue || 'N/A'}
            </div>
        );
    }, []);

    const languagesTemplate = useCallback((props) => {
        const displayValue = props.jobSeekerLanguages || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {displayValue || 'N/A'}
            </div>
        );
    }, []);

    const veteranStatusTemplate = useCallback((props) => {
        const row = props;
        const veteranStatus = row.jobSeekerVeteranStatus || '';
        return (
            <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {veteranStatus || 'N/A'}
            </div>
        );
    }, []);

    const actionsTemplate = useCallback((props) => {
        const row = props;
        // Handle both populated jobseekerId object and jobseekerId string
        const jobSeekerData = row.jobseekerId || row.jobSeekerId;
        
        return (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <ButtonComponent 
                    cssClass="e-primary e-small" 
                    onClick={() => openMeetingDetailModal(row._id)}
                    aria-label="View meeting record details"
                >
                    View Details
                </ButtonComponent>
                {jobSeekerData && (
                    <ButtonComponent 
                        cssClass="e-outline e-primary e-small" 
                        onClick={() => {
                            const jobSeeker = typeof jobSeekerData === 'object' ? jobSeekerData : { _id: jobSeekerData };
                            window.setTimeout(() => {
                                setSelectedJobSeekerForModal(jobSeeker);
                                setSelectedEventIdForModal(row.eventId?._id || row.eventId || null);
                                setShowJobSeekerModal(true);
                            }, 0);
                        }}
                        aria-label="View job seeker profile"
                    >
                        View Job Seeker Profile
                    </ButtonComponent>
                )}
                {(row.jobSeekerResumeId || row.jobSeekerResumeUrl) ? (
                    <ButtonComponent
                        cssClass="e-outline e-primary e-small"
                        onClick={() => openResumeInNewTab(row.jobSeekerResumeId || null, row.jobSeekerResumeUrl || null)}
                        aria-label="View job seeker resume"
                    >
                        View Resume
                    </ButtonComponent>
                ) : null}
            </div>
        );
    }, [openMeetingDetailModal, setSelectedJobSeekerForModal, setShowJobSeekerModal]);

    if (loading || !user) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <AdminHeader 
                brandingLogo={event?.logoUrl || event?.logo || ''}
                brandingLogoAlt={event?.logoAltText || ''}
                secondaryLogo={booth?.logoUrl || booth?.companyLogo || ''}
                secondaryLogoAlt={booth?.logoAltText || ''}
            />
            <div className="dashboard-layout">
                <AdminSidebar active="meeting-records" />
                <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                    <div className="dashboard-content">
                        <div className="meeting-records-container">
                        <div className="page-header">
                            <h1>Meeting Records</h1>
                            {infoBannerMessage && (
                                <div className="info-banner" style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
                                    <span>{infoBannerMessage}</span>
                                </div>
                            )}
                            {supportsMeetingAiTab && (
                                <div className="mr-tab-bar" role="tablist" aria-label="Meeting records view mode">
                                    <button
                                        role="tab"
                                        aria-selected={activeTab === 'list'}
                                        className={`mr-tab-btn${activeTab === 'list' ? ' mr-tab-btn--active' : ''}`}
                                        onClick={() => setActiveTab('list')}
                                    >
                                        All Meeting Records
                                    </button>
                                    <button
                                        role="tab"
                                        aria-selected={activeTab === 'ai-search'}
                                        className={`mr-tab-btn${activeTab === 'ai-search' ? ' mr-tab-btn--active' : ''}`}
                                        onClick={() => setActiveTab('ai-search')}
                                    >
                                        ✦ AI Search
                                    </button>
                                </div>
                            )}
                            {(!supportsMeetingAiTab || activeTab === 'list') && (
                            <div className="header-actions">
                                {['Admin', 'GlobalSupport', 'AdminEvent', 'Recruiter'].includes(user?.role) && (
                                    <>
                                        <ButtonComponent 
                                            cssClass="e-outline e-primary e-small" 
                                            onClick={handleSelectAll}
                                            aria-label="Select all items on current page"
                                        >
                                            Select All
                                        </ButtonComponent>
                                        <ButtonComponent 
                                            cssClass="e-outline e-primary e-small" 
                                            onClick={handleDeselectAll}
                                            disabled={selectedRecords.length === 0}
                                            aria-label="Deselect all items"
                                        >
                                            Deselect All
                                        </ButtonComponent>
                                    </>
                                )}
                                {['Admin', 'GlobalSupport', 'AdminEvent'].includes(user?.role) && (
                                    <ButtonComponent 
                                        cssClass="e-danger e-small"
                                        onClick={handleBulkDelete}
                                        disabled={selectedRecords.length === 0 || isDeleting}
                                        aria-label={`Delete ${selectedRecords.length} selected item(s)`}
                                    >
                                        {isDeleting ? 'Deleting...' : `Delete Selected (${selectedRecords.length})`}
                                    </ButtonComponent>
                                )}
                                <ButtonComponent 
                                    cssClass="e-primary"
                                    onClick={handleExportResumes}
                                    disabled={isExportingResumes || loadingData}
                                    aria-label="Export job seeker resumes as zip file"
                                >
                                    {isExportingResumes ? 'Exporting Resumes...' : 'Export Resumes'}
                                </ButtonComponent>
                                <ButtonComponent 
                                    cssClass="e-primary"
                                    onClick={handleExport}
                                    disabled={loadingData}
                                >
                                    Export CSV
                                </ButtonComponent>
                            </div>
                            )}
                        </div>

                        {supportsMeetingAiTab && activeTab === 'ai-search' && (
                            <div className="mr-ai-tab-content">
                                <AdvancedJobSeekerSearch
                                    mode="meeting"
                                    onViewMeetingRecord={openMeetingDetailModal}
                                />
                            </div>
                        )}

                        {(!supportsMeetingAiTab || activeTab === 'list') && (
                        <>

                        {/* Statistics Cards */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Total Meetings</h3>
                                <div className="stat-value">{stats.totalMeetings}</div>
                            </div>
                            <div className="stat-card">
                                <h3>Completed</h3>
                                <div className="stat-value">{stats.completedMeetings}</div>
                            </div>
                            <div className="stat-card">
                                <h3>Avg Duration</h3>
                                <div className="stat-value">
                                    {stats.averageDuration && !isNaN(stats.averageDuration) 
                                        ? formatDuration(Math.round(stats.averageDuration)) 
                                        : 'N/A'}
                                </div>
                            </div>
                            <div className="stat-card">
                                <h3>Avg Rating</h3>
                                <div className="stat-value">
                                    {stats.averageRating ? `${stats.averageRating.toFixed(1)}/5` : 'N/A'}
                                </div>
                            </div>
                            <div className="stat-card">
                                <h3>Messages Left</h3>
                                <div className="stat-value">{stats.messagesLeft}</div>
                            </div>
                        </div>

                        {/* Search and Filter Row */}
                        <div className="mr-filters-row" style={{ marginBottom: 12, paddingLeft: '20px', paddingRight: '20px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Event Filter - show for all roles */}
                            <div style={{ width: '200px', flexShrink: 0 }}>
                                <DropDownListComponent
                                    id="event-filter-dropdown"
                                    dataSource={[{ value: '', text: 'All Events' }, ...events.map(e => ({ value: e._id, text: e.name }))]}
                                    fields={{ value: 'value', text: 'text' }}
                                    value={filters.eventId}
                                    change={(e) => handleFilterChange('eventId', e.value || '')}
                                    placeholder="All Events"
                                    cssClass="filter-dropdown"
                                    popupHeight="300px"
                                    width="100%"
                                />
                            </div>
                            {/* Booth Filter - only for Admin/GlobalSupport; Recruiters see only their assigned booth automatically */}
                            {['Admin', 'GlobalSupport'].includes(user?.role) && (
                                <div style={{ width: '200px', flexShrink: 0 }}>
                                    <DropDownListComponent
                                        id="booth-filter-dropdown"
                                        dataSource={[{ value: '', text: 'All Booths' }, ...booths.map(b => ({ value: b._id, text: b.name }))]}
                                        fields={{ value: 'value', text: 'text' }}
                                        value={filters.boothId}
                                        change={(e) => handleFilterChange('boothId', e.value || '')}
                                        placeholder="All Booths"
                                        cssClass="filter-dropdown"
                                        popupHeight="300px"
                                        width="100%"
                                    />
                                </div>
                            )}
                            {/* Status Filter - all roles */}
                            <div style={{ width: '200px', flexShrink: 0 }}>
                                <label htmlFor="status-filter-select" className="sr-only">Filter by status</label>
                                <select
                                    id="status-filter-select"
                                    className="mr-status-filter"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Search Section */}
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                                <div style={{ marginBottom: 0 }}>
                                    <label htmlFor="meeting-records-search-input" className="sr-only">Search meeting records</label>
                                    <input
                                        ref={searchInputRef}
                                        id="meeting-records-search-input"
                                        type="text"
                                        defaultValue={filters.search}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSearch();
                                            }
                                        }}
                                        placeholder="Search by name, email, or any field..."
                                        style={{ width: '300px', marginBottom: 0, padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                        className="mr-search-input-native"
                                    />
                                </div>
                                <ButtonComponent
                                    cssClass="e-primary e-small"
                                    onClick={handleSearch}
                                    disabled={loadingData}
                                    aria-label="Search meeting records"
                                    style={{ minWidth: '80px', height: '44px' }}
                                >
                                    Search
                                </ButtonComponent>
                                {filters.search && (
                                    <ButtonComponent
                                        cssClass="e-outline e-primary e-small"
                                        onClick={handleClearSearch}
                                        disabled={loadingData}
                                        aria-label="Clear search"
                                        style={{ minWidth: '70px', height: '44px' }}
                                    >
                                        Clear
                                    </ButtonComponent>
                                )}
                            </div>
                        </div>

                        {/* Select All Pages Banner */}
                        {selectedRecords.length === meetingRecords.length && meetingRecords.length > 0 && !selectAllPages && pagination.totalRecords > meetingRecords.length && (
                            <div style={{
                                background: '#e3f2fd',
                                padding: '12px 20px',
                                borderRadius: '8px',
                                marginBottom: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                border: '1px solid #90caf9'
                            }}>
                                <span style={{ color: '#1565c0', fontWeight: '500' }}>
                                    All {meetingRecords.length} records on this page are selected.
                                </span>
                                <ButtonComponent 
                                    cssClass="e-primary e-small"
                                    onClick={handleSelectAllPages}
                                >
                                    Select all {pagination.totalRecords} records
                                </ButtonComponent>
                            </div>
                        )}

                        {selectAllPages && (
                            <div style={{
                                background: '#c8e6c9',
                                padding: '12px 20px',
                                borderRadius: '8px',
                                marginBottom: '16px',
                                border: '1px solid #81c784'
                            }}>
                                <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                                    ✓ All {selectedRecords.length} records across all pages are selected.
                                </span>
                            </div>
                        )}

                        {/* Data Grid */}
                        <div className="data-grid-container" style={{ position: 'relative' }}>
                            {loadingData && (
                                <div className="mr-grid-loading-overlay">
                                    <div className="mr-loading-container">
                                        <div className="mr-loading-spinner" aria-label="Loading meeting records" role="status" aria-live="polite"></div>
                                        <div className="mr-loading-text">Loading meeting records...</div>
                                    </div>
                                </div>
                            )}
                            <GridComponent
                                ref={gridRef}
                                dataSource={gridDataSource}
                                allowPaging={false}
                                allowSorting={true}
                                allowFiltering={true}
                                enablePersistence={false}
                                filterSettings={gridFilterSettings}
                                showColumnMenu={true}
                                showColumnChooser={true}
                                allowResizing={true}
                                allowReordering={true}
                                toolbar={gridToolbar}
                                selectionSettings={gridSelectionSettings}
                                enableHover={true}
                                allowRowDragAndDrop={false}
                                rowSelected={handleRowSelected}
                                rowDeselected={handleRowSelected}
                            >
                                    <ColumnsDirective>
                                    {['Admin', 'GlobalSupport', 'AdminEvent', 'Recruiter'].includes(user?.role) && (
                                        <ColumnDirective {...SYNC_GRID_CHECKBOX_COLUMN_PROPS} />
                                    )}
                                    <ColumnDirective field='jobSeekerAvatarUrl' headerText='Photo' width='110' textAlign='Center' allowFiltering={false} allowSorting={false} showColumnMenu={false} showInColumnChooser={true} template={avatarTemplate} freeze='Left' />
                                    <ColumnDirective field='jobSeekerName' headerText='Job Seeker' width='180' clipMode='EllipsisWithTooltip' template={jobSeekerTemplate} allowFiltering={true} type='string' textAlign='Center' freeze='Left' />
                                    <ColumnDirective field='id' headerText='' width='0' isPrimaryKey={true} visible={false} showInColumnChooser={false} />
                                    {/* Recruiter, Booth, Status, Actions, Meeting Notes and Rating sit on the left for recruiters (Sprint 7) */}
                                    <ColumnDirective field='recruiterName' headerText='Recruiter' width='150' clipMode='EllipsisWithTooltip' template={recruiterTemplate} allowFiltering={true} textAlign='Center' />
                                    <ColumnDirective field='boothName' headerText='Booth' width='150' clipMode='EllipsisWithTooltip' template={boothTemplate} allowFiltering={true} textAlign='Center' />
                                    <ColumnDirective field='statusLabel' headerText='Status' width='130' textAlign='Center' template={statusTemplate} allowFiltering={true} type='string' />
                                    <ColumnDirective
                                        headerText='Actions'
                                        width='280' 
                                        allowSorting={false} 
                                        allowFiltering={false}
                                        template={actionsTemplate}
                                        showInColumnChooser={true}
                                        visible={true}
                                    />
                                    <ColumnDirective field='recruiterFeedback' headerText='Meeting Notes' width='300' clipMode='EllipsisWithTooltip' template={meetingNotesTemplate} allowFiltering={true} type='string' textAlign='Center' />
                                    <ColumnDirective field='recruiterRating' headerText='Rating' width='150' textAlign='Center' template={ratingTemplate} allowFiltering={true} />
                                    <ColumnDirective field='appliedPosition' headerText='Position' width='180' clipMode='EllipsisWithTooltip' template={appliedPositionTemplate} allowFiltering={true} type='string' textAlign='Center' />
                                    <ColumnDirective field='appliedLocation' headerText='Position Location' width='180' clipMode='EllipsisWithTooltip' template={appliedLocationTemplate} allowFiltering={true} type='string' textAlign='Center' />
                                    <ColumnDirective field='jobSeekerEmail' headerText='Job Seeker Email' width='220' clipMode='EllipsisWithTooltip' template={jobSeekerEmailTemplate} allowFiltering={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerPhone' headerText='Phone' width='150' clipMode='EllipsisWithTooltip' template={phoneTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerCity' headerText='Location' width='150' clipMode='EllipsisWithTooltip' template={locationTemplate} allowFiltering={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerHeadline' headerText='Headline' width='200' clipMode='EllipsisWithTooltip' template={headlineTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerKeywords' headerText='Keywords' width='200' clipMode='EllipsisWithTooltip' template={keywordsTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerPrimaryExperience' headerText='Primary Job Experience' width='210' clipMode='EllipsisWithTooltip' template={primaryJobExperienceTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerWorkLevel' headerText='Work Experience Level' width='180' clipMode='EllipsisWithTooltip' template={workExperienceLevelTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerEducationLevel' headerText='Highest Education Level' width='200' clipMode='EllipsisWithTooltip' template={educationLevelTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerEmploymentTypes' headerText='Employment Types' width='200' clipMode='EllipsisWithTooltip' template={employmentTypesTemplate} allowFiltering={true} type='string' visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerLanguages' headerText='Language(s)' width='200' clipMode='EllipsisWithTooltip' template={languagesTemplate} allowFiltering={true} type='string' visible={true} textAlign='Center' />
                                    <ColumnDirective field='jobSeekerVeteranStatus' headerText='Veteran/Military Status' width='200' clipMode='EllipsisWithTooltip' template={veteranStatusTemplate} allowFiltering={true} visible={true} textAlign='Center' />
                                    <ColumnDirective field='startTime' headerText='Start Time' width='180' clipMode='EllipsisWithTooltip' template={startTimeTemplate} allowFiltering={true} type='string' textAlign='Center' />
                                    <ColumnDirective field='duration' headerText='Duration' width='120' textAlign='Center' template={durationTemplate} allowFiltering={true} />
                                    <ColumnDirective field='interpreterName' headerText='Interpreter' width='150' clipMode='EllipsisWithTooltip' template={interpreterTemplate} allowFiltering={true} textAlign='Center' />
                                    <ColumnDirective field='eventName' headerText='Event' width='150' clipMode='EllipsisWithTooltip' template={eventTemplate} allowFiltering={true} textAlign='Center' />
                                </ColumnsDirective>
                                <GridInject services={[Sort, Filter, GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze]} />
                            </GridComponent>
                            
                            {/* Custom Pagination Footer */}
                            {pagination.totalPages > 0 && (
                                <div className="custom-pagination" style={{
                                    display: 'flex', 
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                    backgroundColor: '#f9fafb',
                                    borderTop: '1px solid #e5e7eb',
                                    marginTop: '0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <span style={{ fontSize: '14px', color: '#374151' }}>
                                            Rows per page:
                                        </span>
                                        <select
                                            value={filters.limit}
                                            onChange={(e) => {
                                                const newSize = parseInt(e.target.value);
                                                handlePageSizeChange(newSize);
                                            }}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                fontSize: '14px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '14px', color: '#374151' }}>
                                            Page {pagination.currentPage} of {pagination.totalPages || 1} ({pagination.totalRecords || 0} total)
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button
                                            onClick={() => {
                                                if (pagination.currentPage > 1) {
                                                    handlePageChange(1);
                                                }
                                            }}
                                            disabled={pagination.currentPage <= 1 || loadingData}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: pagination.currentPage <= 1 ? '#f3f4f6' : '#fff',
                                                cursor: pagination.currentPage <= 1 ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                color: pagination.currentPage <= 1 ? '#9ca3af' : '#374151'
                                            }}
                                            title="First Page"
                                        >
                                            ⟨⟨
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (pagination.currentPage > 1) {
                                                    handlePageChange(pagination.currentPage - 1);
                                                }
                                            }}
                                            disabled={pagination.currentPage <= 1 || loadingData}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: pagination.currentPage <= 1 ? '#f3f4f6' : '#fff',
                                                cursor: pagination.currentPage <= 1 ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                color: pagination.currentPage <= 1 ? '#9ca3af' : '#374151'
                                            }}
                                            title="Previous Page"
                                        >
                                            ⟨ Prev
                                        </button>
                                        
                                        <input
                                            type="number"
                                            min="1"
                                            max={pagination.totalPages || 1}
                                            value={pagination.currentPage}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val >= 1 && val <= (pagination.totalPages || 1)) {
                                                    handlePageChange(val);
                                                }
                                            }}
                                            style={{
                                                width: '60px',
                                                padding: '6px 8px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                fontSize: '14px',
                                                textAlign: 'center'
                                            }}
                                        />
                                        
                                        <button
                                            onClick={() => {
                                                if (pagination.currentPage < (pagination.totalPages || 1)) {
                                                    handlePageChange(pagination.currentPage + 1);
                                                }
                                            }}
                                            disabled={pagination.currentPage >= (pagination.totalPages || 1) || loadingData}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: pagination.currentPage >= (pagination.totalPages || 1) ? '#f3f4f6' : '#fff',
                                                cursor: pagination.currentPage >= (pagination.totalPages || 1) ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                color: pagination.currentPage >= (pagination.totalPages || 1) ? '#9ca3af' : '#374151'
                                            }}
                                            title="Next Page"
                                        >
                                            Next ⟩
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (pagination.currentPage < (pagination.totalPages || 1)) {
                                                    handlePageChange(pagination.totalPages || 1);
                                                }
                                            }}
                                            disabled={pagination.currentPage >= (pagination.totalPages || 1) || loadingData}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: pagination.currentPage >= (pagination.totalPages || 1) ? '#f3f4f6' : '#fff',
                                                cursor: pagination.currentPage >= (pagination.totalPages || 1) ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                color: pagination.currentPage >= (pagination.totalPages || 1) ? '#9ca3af' : '#374151'
                                            }}
                                            title="Last Page"
                                        >
                                            ⟩⟩
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        </>
                        )}
                        </div>
                    </div>
                </main>
            </div>

            {confirmDeleteOpen && (
            <DialogComponent
                width="450px"
                isModal={true}
                showCloseIcon={true}
                visible={true}
                header="Delete Meeting Records"
                closeOnEscape={true}
                close={cancelBulkDelete}
                cssClass="meeting-records-delete-dialog"
                buttons={[
                    {
                        buttonModel: {
                            content: 'Cancel',
                            isPrimary: false,
                            cssClass: 'e-outline e-primary'
                        },
                        click: () => {
                            cancelBulkDelete();
                        }
                    },
                    {
                        buttonModel: {
                            content: 'Delete',
                            isPrimary: true,
                            cssClass: 'e-danger'
                        },
                        click: () => {
                            confirmBulkDelete();
                        }
                    }
                ]}
            >
                <p style={{ margin: 0, lineHeight: '1.5' }}>
                    Are you sure you want to delete <strong>{selectedRecords.length}</strong> meeting record(s)? This action cannot be undone.
                </p>
            </DialogComponent>
            )}

            {/* Syncfusion ToastComponent */}
            <ToastComponent 
                ref={(toast) => toastRef.current = toast}
                position={{ X: 'Right', Y: 'Bottom' }}
                showProgressBar={true}
                timeOut={3000}
                newestOnTop={true}
            />

            {showJobSeekerModal && selectedJobSeekerForModal && (
                <JobSeekerProfileModal
                    isOpen
                    onClose={() => {
                        setShowJobSeekerModal(false);
                        setSelectedJobSeekerForModal(null);
                        setSelectedEventIdForModal(null);
                    }}
                    jobSeeker={selectedJobSeekerForModal}
                    eventId={selectedEventIdForModal}
                />
            )}

            {showMeetingDetailModal && selectedMeetingRecordId && (
                <MeetingRecordDetailModal
                    isOpen
                    onClose={closeMeetingDetailModal}
                    meetingRecordId={selectedMeetingRecordId}
                />
            )}

        </div>
    );
}
